import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Row, Col, Popconfirm } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormInput from 'components-desktop/FormInput';
import FormInputNumber from 'components-desktop/FormInputNumber';
import FormSelect from 'components-desktop/FormSelect';
import FormTextArea from 'components-desktop/FormTextArea';
import Panel from 'components-desktop/Panel';
import helpers from 'misc/helpers';
import TableInvoicePayments from './components/TableInvoicePayments';
import FormSearchApplyInvoice from './components/FormSearchApplyInvoice';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class PaymentDetailsPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      payment: null,
      isSaveButtonEnable: true,
      isDeleteButtonEnable: true,

      tableKey: null,
      invoicePayments: null,
    };
  }

  componentDidMount() {
    this.id = this.parseQueryParam('id', this.props.location);
    this.getPayment();
  }

  getTitle() {
    return 'Payment Details';
  }

  async getPayment() {
    const params = { id: this.id };
    const payment = await this.callServiceForRemoteObject('/payment/get_model', params);

    this.setState({
      payment,
    });

    // After getting the payment, also get its relevant infos.
    const params2 = { paymentId: this.id };
    const promise1 = this.callServiceForRemoteObjects('/invoice_payment/get_list_by_payment', params2);

    const results = await Promise.all([promise1]);

    this.setState({
      tableKey: `${Date.now()}_${Math.random()}`,
      invoicePayments: results[0],
    });
  }

  async savePayment(values) {
    try {
      this.setState({
        isSaveButtonEnable: false,
      });

      const params = {
        id: this.id,
        checkNumber: helpers.extraFormInputValue(values, 'checkNumber'),
        paymentMethod: helpers.extraFormSelectValue(values, 'paymentMethod'),
        checkDate: helpers.extraFormDateValue(values, 'checkDate'),
        receivedDate: helpers.extraFormDateValue(values, 'receivedDate'),
        amount: helpers.extraFormInputNumberValue(values, 'amount'),
        remark: helpers.extraFormTextAreaValue(values, 'remark'),
      };
      await this.callService('/payment/update', params);

      this.notifySuccess('Success', 'Payment has been updated.');
    } finally {
      this.setState({
        isSaveButtonEnable: true,
      });
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.savePayment(values);
      }
    });
  }

  onResetClick() {
    window.location.reload();
  }

  async onDeleteClick() {
    try {
      this.setState({
        isDeleteButtonEnable: false,
      });

      const params = { id: this.id };
      await this.callService('/payment/delete', params);

      this.notifySuccess('Success', 'Payment has been deleted.');

      // Go to the search payments page.
      setTimeout(() => {
        window.location = `/payments/search_payments`;
      }, 2000);
    } finally {
      this.setState({
        isDeleteButtonEnable: true,
      });
    }
  }

  onInvoicePaymentDelete(id) {
    this.getPayment();
  }

  onInvoicePaymentApply() {
    this.getPayment();
  }

  isInRenderableState() {
    return !!this.state.payment;
  }

  renderContent() {
    const { form } = this.props;
    const {
      payment,
      isSaveButtonEnable,
      isDeleteButtonEnable,
      tableKey,
      invoicePayments,
    } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="yellow_gold"
          title={`Payment Details - Check No. ${payment.getString('checkNumber')}`}
          icon="money-collect"
          changeHistoryTable="Payment"
          changeHistoryRowId={Number(this.id)}
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="companyId"
                    label="Company"
                    disabled
                    initialValue={payment.getString('companyName')}
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="checkNumber"
                    label="Check Number"
                    max={120}
                    initialValue={payment.getString('checkNumber')}
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="paymentMethod"
                    label="Payment Method"
                    placeholder="Select..."
                    allowClear={false}
                    initialValue={payment.getString('paymentMethod')}
                    options={['Check', 'Other']}
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="checkDate"
                    label="Check Date"
                    placeholder="mm/dd/yyyy"
                    initialValue={payment.getMoment('checkDate')}
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="receivedDate"
                    label="Received Date"
                    placeholder="mm/dd/yyyy"
                    initialValue={payment.getMoment('receivedDate')}
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInputNumber
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="amount"
                    label="Total Amount"
                    placeholder="12.34"
                    min={-999999999} // Sunshine requested allowing negative payment amount.
                    step={0.01}
                    precision={2}
                    // formatter={value => `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    // parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    initialValue={payment.getDouble('amount').toString()}
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                  <FormTextArea
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="remark"
                    label="Remark"
                    rows={3}
                    max={2000}
                    initialValue={payment.getString('remark')}
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                margin="0 4px 0 0"
                backgroundColor="yellow_gold"
                fontSize="14px"
                type="primary"
                loading={!isSaveButtonEnable}
                htmlType="submit"
              >
                SAVE
              </CustomButton>

              <styledComp.AntdButton
                width="wrap_content"
                height="32px"
                margin="0 64px 0 0"
                fontSize="14px"
                type="default"
                onClick={this.onResetClick}
              >
                RESET
              </styledComp.AntdButton>

              <Popconfirm
                title="Are you sure you want to delete this payment?"
                placement="top"
                okText="Confirm"
                cancelText="Cancel"
                onConfirm={this.onDeleteClick}
              >
                <styledComp.AntdButton
                  width="wrap_content"
                  height="32px"
                  fontSize="14px"
                  type="danger"
                  loading={!isDeleteButtonEnable}
                >
                  DELETE
                </styledComp.AntdButton>
              </Popconfirm>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>

        {invoicePayments &&
          <TableInvoicePayments
            key={`${tableKey}_payments`}
            width="match_parent"
            height="wrap_content"
            margin="0 0 24px 0"
            title="All Invoices Paid by This Payment"
            invoicePayments={invoicePayments}
            onInvoicePaymentDelete={this.onInvoicePaymentDelete}
          />
        }

        <FormSearchApplyInvoice
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          payment={payment}
          onInvoicePaymentApply={this.onInvoicePaymentApply}
        />
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
PaymentDetailsPage.propTypes = propTypes;

export default Form.create()(PaymentDetailsPage);
